import React from 'react';
import { createRoot } from 'react-dom/client';
import smoothscroll from 'smoothscroll-polyfill';

// React 16.8.6 polyfill for useId — required by some upgraded DS components.
import 'utils/reactUseIdPolyfill';

import 'utils/fetchInterceptor';

// NBC Design System tokens — must be imported BEFORE component styles
// so themed CSS variables are available when ThemeProvider applies data-theme.
import '@nbc-design/tokens/dist/styles/theme.css';
import '@nbc-design/tokens/dist/styles/legacy/light.css';

import './tailwind-compiled.css';
import './styles/product-form-overrides.scss';

import { ThemeProvider } from '@nbc-design/theme-provider';
import configureStore from 'globalRedux/configureStore';
import getLogger from 'utils/getLogger';
import { initDatadogRum, wireDatadogRumConsent } from 'utils/initDatadogRum';
import Main from './components/Main';

// Suppress known PropTypes warnings from bnc-react-forms when API auth is unavailable.
// Guarded so repeated HMR reloads don't stack multiple console.error wrappers, and the
// filter is narrowed to a few known ProductForm messages — everything else passes through.
if (process.env.NODE_ENV === 'development') {
  const PATCH_FLAG = '__baoConsoleErrorFiltered__';
  const flaggedConsole = (console as unknown) as Record<string, unknown>;

  if (!flaggedConsole[PATCH_FLAG]) {
    const originalError = console.error;
    const isKnownProductFormWarning = (msg: string) =>
      (msg.includes('Failed prop type') && msg.includes('ProductFormProvider')) ||
      (msg.includes('Failed child context type') && msg.includes('ProductFormProvider')) ||
      (msg.includes('Failed context type') && msg.includes('ProductFormConsumer'));

    console.error = (...args: any[]) => {
      const msg = typeof args[0] === 'string' ? args[0] : '';
      if (isKnownProductFormWarning(msg)) return;
      originalError.apply(console, args);
    };
    flaggedConsole[PATCH_FLAG] = true;
  }
}

// polyfill scrollTo, scrollIntoView, etc...
// see https://caniuse.com/#feat=element-scroll-methods
smoothscroll.polyfill();

const logger = getLogger();
logger.log(`
-------------------------------------
  ######        ###         #####
  ##   ##      ## ##      ##     ##  
  ######      #######     ##     ##
  ##   ##    ##     ##    ##     ##
  ######    ##       ##     #####
-------------------------------------
`);

initDatadogRum();
wireDatadogRumConsent();

const store = configureStore();

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <ThemeProvider theme="legacy" colorScheme="light">
    <Main store={store} />
  </ThemeProvider>,
);
